import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { createRootRouteWithContext, createRoute, createRouter, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { api, fetchMe, setCsrfToken } from './api.js';
import { meQueryKey, MeProvider, useMeQuery } from './auth.js';
import { AuditPage } from './pages/AuditPage.js';
import { GamesPage } from './pages/GamesPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { PartnersPage } from './pages/PartnersPage.js';
import { PaymentsPage } from './pages/PaymentsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { StatementDetailPage, StatementsPage } from './pages/StatementsPage.js';
import { SteamPage, useSteamStatus } from './pages/SteamPage.js';
import { parseDashboardSearch } from './pages/dashboard-search.js';
import { LoginPage } from './pages/LoginPage.js';
import { UsersPage } from './pages/UsersPage.js';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });

/** Every page below needs a signed-in session; anything else goes to /login. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData({ queryKey: meQueryKey, queryFn: fetchMe });
    if (!me || me.stage !== 'active') throw redirect({ to: '/login', search: { from: location.href } as never });
  },
  component: AppLayout,
});

function AppLayout() {
  const me = useMeQuery();
  const client = useQueryClient();
  const navigate = useNavigate();
  if (!me.data?.user) return null;
  const signOut = async () => {
    await api('POST', '/api/auth/logout');
    setCsrfToken(null);
    client.clear();
    await navigate({ to: '/login' });
  };
  const nav = 'rounded px-2 py-1 hover:bg-slate-200 dark:hover:bg-slate-800 [&.active]:font-semibold';
  return (
    <MeProvider me={me.data}>
      <div className="min-h-screen">
        <header className="flex items-center gap-4 border-b border-slate-200 px-6 py-3 dark:border-slate-800">
          <span className="font-semibold">Steam Revenue</span>
          <nav className="flex gap-1 text-sm">
            <Link to="/" className={nav} activeOptions={{ includeSearch: false }}>Dashboard</Link>
            <Link to="/games" className={nav}>Games</Link>
            <Link to="/partners" className={nav}>Partners</Link>
            <Link to="/reports" className={nav}>Reports</Link>
            <Link to="/payments" className={nav}>Payments</Link>
            <Link to="/statements" className={nav}>Statements</Link>
            <Link to="/invoices" className={nav}>Invoices</Link>
            <Link to="/steam" className={nav}>Steam sync</Link>
            <Link to="/users" className={nav}>Users</Link>
            <Link to="/audit" className={nav}>Audit</Link>
            <Link to="/settings" className={nav}>Settings</Link>
          </nav>
          <span className="ml-auto text-sm text-slate-500">
            {me.data.user.email} ({me.data.user.role})
          </span>
          <button className="text-sm underline" onClick={() => void signOut()}>
            Sign out
          </button>
        </header>
        <SyncBanner />
        <main className="px-6 py-6">
          <Outlet />
        </main>
      </div>
    </MeProvider>
  );
}

/** Shown on every page while the Steam sync is failing or not set up. */
function SyncBanner() {
  const status = useSteamStatus();
  const s = status.data;
  if (!s) return null;
  const message = !s.key
    ? 'The Steam Financial API key is not configured; no sales are imported.'
    : s.sync && s.sync.consecutiveFailures > 0
      ? `The Steam sync failed ${s.sync.consecutiveFailures} time(s) in a row: ${s.sync.lastError ?? ''}`
      : null;
  if (!message) return null;
  return (
    <div role="alert" className="border-b border-amber-400 bg-amber-50 px-6 py-2 text-sm dark:bg-amber-950">
      {message} <Link to="/steam" className="underline">Steam sync</Link>
    </div>
  );
}

/** Charts load with the dashboard only. */
const DashboardPage = lazy(() => import('./pages/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  validateSearch: parseDashboardSearch,
  component: function DashboardRoute() {
    const search = homeRoute.useSearch();
    const navigate = homeRoute.useNavigate();
    return (
      <Suspense fallback={<p className="text-sm text-slate-500">Loading...</p>}>
        <DashboardPage params={search} onSearch={(next) => void navigate({ search: next, replace: true })} />
      </Suspense>
    );
  },
});
const usersRoute = createRoute({ getParentRoute: () => appRoute, path: '/users', component: UsersPage });
const auditRoute = createRoute({ getParentRoute: () => appRoute, path: '/audit', component: AuditPage });
const gamesRoute = createRoute({ getParentRoute: () => appRoute, path: '/games', component: GamesPage });
const steamRoute = createRoute({ getParentRoute: () => appRoute, path: '/steam', component: SteamPage });
const reportsRoute = createRoute({ getParentRoute: () => appRoute, path: '/reports', component: ReportsPage });
const partnersRoute = createRoute({ getParentRoute: () => appRoute, path: '/partners', component: PartnersPage });
const paymentsRoute = createRoute({ getParentRoute: () => appRoute, path: '/payments', component: PaymentsPage });
const statementsRoute = createRoute({ getParentRoute: () => appRoute, path: '/statements', component: StatementsPage });
const statementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/statements/$statementId',
  component: function StatementRoute() {
    const { statementId } = statementRoute.useParams();
    return <StatementDetailPage key={statementId} statementId={statementId} />;
  },
});
const invoicesRoute = createRoute({ getParentRoute: () => appRoute, path: '/invoices', component: InvoicesPage });
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsPage });

const routeTree = rootRoute.addChildren([loginRoute, appRoute.addChildren([
  homeRoute, gamesRoute, partnersRoute, reportsRoute, paymentsRoute, statementsRoute, statementRoute, invoicesRoute, settingsRoute, steamRoute, usersRoute, auditRoute,
])]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient } });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
